import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "@tanstack/react-router";
import { EXACT_PAGE_LINK } from "@/common/lib/navigation/active-options";
import { ChevronsUpDown, Check, Plus, LayoutGrid, User } from "lucide-react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  ListLedgersDocument,
  CreateLedgerDocument,
  GetCurrentUserDocument,
  type CreateLedgerMutationVariables,
} from "@/graphql/definitions";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/common/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/common/components/ui/command";
import { Button } from "@/common/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/common/components/ui/dialog";
import { LedgerForm } from "@/features/ledger-list/components/ledger-form";
import { LedgerVisibilityIcon } from "./ledger-visibility-icon";
import { toast } from "sonner";
import { cn } from "@/common/lib/utils/utils";
import { decodeLedgerId, parseLedgerFullName } from "@/common/lib/utils/encode";
import { groupLedgersByOwner } from "@/common/lib/utils/ledger-utils";
import { useTranslations } from "@/common/hooks/use-translations";
import {
  getErrorMessageKey,
  useErrorMessage,
} from "@/common/lib/errors/error-message";
import { Authenticated } from "../authenticated";
import { useReactNativeContext } from "@/common/providers/react-native-bridge-provider";
import { useSidebar } from "@/common/components/ui/sidebar";

interface LedgerSwitcherProps {
  currentLedgerId: string;
  currentLedgerName: string;
  currentLedgerFullName?: string;
}

interface LedgerSwitcherButtonContentProps {
  currentLedgerFullName: string;
  showSelectIcon?: boolean;
}

/**
 * Button content component for ledger switcher trigger
 * Displays ledger logo, name, and chevron icon
 */
function LedgerSwitcherButtonContent({
  currentLedgerFullName,
  showSelectIcon = true,
}: LedgerSwitcherButtonContentProps) {
  const { owner, repo } = parseLedgerFullName(currentLedgerFullName);

  return (
    <div className="w-full flex items-center justify-between gap-2 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
          <img src="/lgasset/logo.png" alt="" className="h-8 w-8 rounded" />
        </div>
        <div className="flex flex-col items-start min-w-0 group-data-[collapsible=icon]:hidden">
          <Link
            to="/ledger/$username"
            params={{ username: owner ?? "" }}
            className="flex min-h-6 items-center text-xs text-muted-foreground truncate hover:text-foreground transition-colors"
            {...EXACT_PAGE_LINK}
          >
            {owner ?? ""}
          </Link>
          <span className="text-sm font-medium truncate">
            {repo || currentLedgerFullName}
          </span>
        </div>
      </div>
      {showSelectIcon && (
        <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50 group-data-[collapsible=icon]:hidden" />
      )}
    </div>
  );
}

/**
 * Ledger switcher dropdown component
 * Displays current ledger and allows switching between ledgers or creating new ones
 */
function LedgerAuthenticatedSwitcher({
  currentLedgerId,
  currentLedgerName,
  currentLedgerFullName,
}: LedgerSwitcherProps) {
  const { t } = useTranslations();
  const formatError = useErrorMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const [open, setOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const closeMobileDrawer = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Derive fullName from ledgerId if not provided
  const fullName =
    currentLedgerFullName ||
    decodeLedgerId(currentLedgerId).ledgerOwner + "/" + currentLedgerName;

  const { data, loading, error, refetch } = useQuery(ListLedgersDocument, {
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  // A failed read with nothing cached is not "no ledgers" (w4/275); cached
  // ledgers stay listed if only a refresh fails.
  const readFailed = Boolean(error) && !data && !loading;
  const searchRef = React.useRef<HTMLInputElement>(null);
  const [createLedgerMutation, { loading: createLoading }] = useMutation(
    CreateLedgerDocument,
    {
      refetchQueries: [ListLedgersDocument, GetCurrentUserDocument],
    },
  );

  const ledgers = data?.listLedgers || [];
  const groupedLedgers = groupLedgersByOwner(ledgers);
  // Sort alphabetically by owner for consistent display
  const sortedGroups = Array.from(groupedLedgers.entries()).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  const handleSelectLedger = (ledgerId: string) => {
    if (ledgerId !== currentLedgerId) {
      // Extract the current page path after /ledger/owner/name/
      const currentPath = location.pathname;
      const pathMatch = currentPath.match(/^\/ledger\/[^/]+\/[^/]+(.*)$/);
      const pagePath = pathMatch?.[1] || "";

      // Decode ledger ID to get owner and name
      const { ledgerOwner, ledgerName } = decodeLedgerId(ledgerId);

      // Construct new path with same page but different ledger
      const newPath = `/ledger/${ledgerOwner}/${ledgerName}${pagePath}`;

      // Navigate to the same page on the other ledger. Shared account/filter/time
      // must not leak across ledgers unless the destination URL supplies them —
      // retainSearchParams would otherwise copy the previous ledger's selection.
      void navigate({
        to: newPath,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: (prev: any) => ({
          ...prev,
          account: undefined,
          filter: undefined,
          time: undefined,
        }),
      });
    }
    setOpen(false);
    closeMobileDrawer();
  };

  const handleSelectOwner = (owner: string) => {
    void navigate({ to: `/ledger/${owner}` });
    setOpen(false);
    closeMobileDrawer();
  };

  const handleManageLedgers = () => {
    void navigate({ to: "/ledger" });
    setOpen(false);
    closeMobileDrawer();
  };

  const handleCreateLedger = async (data: CreateLedgerMutationVariables) => {
    try {
      const result = await createLedgerMutation({ variables: data });
      setIsCreateDialogOpen(false);
      setOpen(false);
      closeMobileDrawer();
      toast.success(t("page.dashboard.ledgerCreatedSuccess"));

      // Navigate to the new ledger
      if (result.data?.createLedger?.id) {
        const { ledgerOwner, ledgerName } = decodeLedgerId(
          result.data.createLedger.id,
        );
        void navigate({
          to: `/ledger/${ledgerOwner}/${ledgerName}`,
        });
      }
      return true;
    } catch (error) {
      toast.error(formatError(error));
      console.error("Failed to create ledger:", error);
      return false;
    }
  };

  const { owner, repo } = parseLedgerFullName(fullName);

  return (
    <>
      <div className="w-full flex items-center gap-2 min-w-0">
        {/* Part 1: Logo - Link to /ledger */}
        <Link
          to="/ledger"
          aria-label={t("page.dashboard.goToDashboard")}
          className="shrink-0 cursor-pointer"
          {...EXACT_PAGE_LINK}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:opacity-80 transition-opacity">
            <img src="/lgasset/logo.png" alt="" className="h-8 w-8 rounded" />
          </div>
        </Link>

        {/* Parts 2 & 3: Owner link and Repo popover trigger.
            Hidden when the sidebar is collapsed to the icon rail. */}
        <div className="flex flex-col justify-center min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
          {owner && (
            <Link
              to="/ledger/$username"
              params={{ username: owner }}
              className="flex min-h-6 items-center text-xs text-muted-foreground truncate hover:text-foreground transition-colors"
              {...EXACT_PAGE_LINK}
            >
              {owner}
            </Link>
          )}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                role="combobox"
                aria-expanded={open}
                aria-label={t("page.dashboard.switchLedgerFrom", {
                  ledger: repo || fullName,
                })}
                className="justify-start min-w-0 min-h-6 px-0 py-0 h-auto has-[>svg]:px-0 hover:bg-transparent"
              >
                <span className="text-sm font-medium truncate">
                  {repo || fullName}
                </span>
                <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[300px] p-0"
              align="start"
              aria-label={t("page.dashboard.selectLedger")}
            >
              <Command
                label={t("page.dashboard.searchLedgers")}
                className="max-h-[min(340px,var(--radix-popover-content-available-height))] [&_[cmdk-input-wrapper]]:shrink-0"
              >
                <CommandInput
                  ref={searchRef}
                  placeholder={t("page.dashboard.searchLedgers")}
                />
                <CommandList className="min-h-0 flex-1">
                  {readFailed ? (
                    <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
                      <p role="alert" className="text-sm text-muted-foreground">
                        {t(getErrorMessageKey(error))}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // The loading view replaces this button; keep the
                          // keyboard in the picker (as Go to Account does).
                          searchRef.current?.focus();
                          void refetch().catch(() => undefined);
                        }}
                        onKeyDown={(event) => {
                          // cmdk cancels every Enter reaching its root; let
                          // the button's own activation run, once.
                          if (event.key === "Enter") event.stopPropagation();
                        }}
                      >
                        {t("common.tryAgain")}
                      </Button>
                    </div>
                  ) : (
                    <CommandEmpty>
                      {loading
                        ? t("page.dashboard.loadingLedgers")
                        : t("page.dashboard.noLedgersFound")}
                    </CommandEmpty>
                  )}

                  {sortedGroups.map(([owner, ownedLedgers], groupIndex) => (
                    <React.Fragment key={owner}>
                      <CommandGroup>
                        {/* Owner-level item - clickable, navigates to account page */}
                        <CommandItem
                          value={owner}
                          onSelect={() => handleSelectOwner(owner)}
                          className="font-semibold"
                          aria-label={t("page.dashboard.goToAccount", {
                            owner,
                          })}
                        >
                          <User className="mr-2 h-4 w-4" />
                          <span>{owner}</span>
                        </CommandItem>

                        {/* Repository items under this owner - indented */}
                        {ownedLedgers.map((ledger) => {
                          const { repo } = parseLedgerFullName(ledger.fullName);
                          return (
                            <CommandItem
                              key={ledger.id}
                              value={`${ledger.fullName} ${ledger.name}`}
                              onSelect={() => handleSelectLedger(ledger.id)}
                              className="pl-6"
                              // `aria-selected` is cmdk's keyboard highlight;
                              // the ledger in use is marked separately.
                              aria-current={
                                currentLedgerId === ledger.id
                                  ? "true"
                                  : undefined
                              }
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  currentLedgerId === ledger.id
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <span className="text-sm truncate">
                                {repo || ledger.name}
                              </span>
                              <LedgerVisibilityIcon
                                isPrivate={ledger.private}
                                className="ml-auto"
                              />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>

                      {/* Separator between owner groups (not after last) */}
                      {groupIndex < sortedGroups.length - 1 && (
                        <CommandSeparator />
                      )}
                    </React.Fragment>
                  ))}
                </CommandList>

                {/* Keep global actions outside cmdk's scrollable list sizer. */}
                <div className="shrink-0 border-t bg-popover p-1">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-start rounded-sm px-2 py-1.5 text-sm font-normal"
                    onClick={() => {
                      setOpen(false);
                      setIsCreateDialogOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("page.dashboard.createLedger")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-start rounded-sm px-2 py-1.5 text-sm font-normal"
                    onClick={handleManageLedgers}
                  >
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    {t("page.dashboard.manageLedgers")}
                  </Button>
                </div>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Create Ledger Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("page.dashboard.createNewLedger")}</DialogTitle>
            <DialogDescription>
              {t("page.dashboard.createNewLedgerDescription")}
            </DialogDescription>
          </DialogHeader>
          <LedgerForm
            onSubmit={handleCreateLedger}
            isLoading={createLoading}
            onCancel={() => setIsCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export function LedgerSwitcher({
  currentLedgerId,
  currentLedgerName,
  currentLedgerFullName,
}: LedgerSwitcherProps) {
  // Derive fullName from ledgerId if not provided
  const fullName =
    currentLedgerFullName ||
    decodeLedgerId(currentLedgerId).ledgerOwner + "/" + currentLedgerName;

  const { isReactNative } = useReactNativeContext();
  if (isReactNative) {
    return (
      <LedgerSwitcherButtonContent
        currentLedgerFullName={fullName}
        showSelectIcon={false}
      />
    );
  }
  return (
    <Authenticated
      fallback={
        <LedgerSwitcherButtonContent
          currentLedgerFullName={fullName}
          showSelectIcon={false}
        />
      }
    >
      <LedgerAuthenticatedSwitcher
        currentLedgerId={currentLedgerId}
        currentLedgerName={currentLedgerName}
        currentLedgerFullName={fullName}
      />
    </Authenticated>
  );
}
